import { type FC, lazy, Suspense } from "react";

import { SectionHeading } from "@clientFrontend/features/scout/components/SectionHeading";
import { InformationBox } from "@clientFrontend/features/scout/components/InformationBox";

import { ReadMoreButton } from "@root/shared/components/ReadMoreButton";
import { BasicHopedConditionsTable } from "@clientFrontend/features/scout/components/UserDetail/uiParts/BasicHopedConditionsTable";
import type { basicHopedConditionsProps } from "@clientFrontend/features/scout/components/UserDetail/uiParts/BasicHopedConditionsTable";
import { ThoughtsAndSelfPrTable } from "@clientFrontend/features/scout/components/UserDetail/uiParts/ThoughtsAndSelfPrTable";
import type { thoughtsAndSelfPrProps } from "@clientFrontend/features/scout/components/UserDetail/uiParts/ThoughtsAndSelfPrTable";
import { DetailHopedConditionsTable } from "@clientFrontend/features/scout/components/UserDetail/uiParts/DetailHopedConditionsTable";
import type { detailHopedConditionsProps } from "@clientFrontend/features/scout/components/UserDetail/uiParts/DetailHopedConditionsTable";
import type { userProductPhotoProps } from "@clientFrontend/features/scout/components/UserDetail/uiParts/UserProductPhotosCassette";
import { UserSkillsList } from "@clientFrontend/features/scout/components/UserDetail/uiParts/UserSkillsList";
import type { userSkillsProps } from "@clientFrontend/features/scout/components/UserDetail/uiParts/UserSkillsList";
import { UserCareerDefinitionList } from "@clientFrontend/features/scout/components/UserDetail/uiParts/UserCareerDefinitionList";
import { UserAchievementsDefinitionList } from "@clientFrontend/features/scout/components/UserDetail/uiParts/UserAchievementsDefinitionList";
import { UserSchoolsDefinitionList } from "@clientFrontend/features/scout/components/UserDetail/uiParts/UserSchoolsDefinitionList";
import { ActivityHistoriesTable } from "@clientFrontend/features/scout/components/UserDetail/uiParts/ActivityHistoriesTable";
import type { ActivityHistoriesProps } from "@clientFrontend/features/scout/components/UserDetail/uiParts/ActivityHistoriesTable";
import {
  MultiActionInformationBox,
  type MultiActionItem,
  type MinOneArray,
} from "@clientFrontend/features/scout/components/UserDetail/uiParts/MultiActionInformationBox";
import type { UserHiddenButtonContent } from "@clientFrontend/features/scout/types/userHidden";
import { ModelessDialog } from "@clientFrontend/features/scout/components/ModelessDialog";
import { retryLazyLoad } from "@root/shared/utils/retryLazyLoad";
import { PhotoContestLabel } from "@clientFrontend/components/PhotoContestLabel";
import { type PhotoContestInfo } from "@clientFrontend/features/scout/types/photoContestInfo";
import { Header } from "./uiParts/Header";
import type { scoutButtonContentProps, PickupButtonContentProps } from "./uiParts/Header";
import { ResultUserControlButton } from "./uiParts/ResultUserControlButton";
import * as styles from "./styles.css";

const UserProductPhotosCassette = lazy(() =>
  retryLazyLoad({
    fn: () =>
      import("@clientFrontend/features/scout/components/UserDetail/uiParts/UserProductPhotosCassette"),
  }),
);

export type UserDetailModalProps = {
  gaEvents: {
    modal: {
      gaAction: string;
      gaLabel: string;
    };
    scout: {
      gaAction: string;
      gaLabel: string;
    };
    premiumScout: {
      gaAction: string;
      gaLabel: string;
    };
    pickup: {
      gaAction: string;
      gaLabel: string;
    };
    pickupRelease: {
      gaAction: string;
      gaLabel: string;
    };
  };
  displayControlInformation: {
    isHideInfo?: boolean;
    hideInfoReasonText?: string;
    notScoutableReasonText?: string;
    isNotScoutable: boolean;
    scoutIndexUrl?: string;
    isNew: boolean;
    entryText?: string;
    isIconFire: boolean;
  };
  userData: {
    sexAndAgeText: string;
    acceptableReceivedScoutCount?: number;
    currentAccessAt?: string;
    multiActionInfo?: MultiActionItem[];
    photoContestInfo?: PhotoContestInfo[];
  };
  historyInformation: {
    userSchoolInfoList?: { term: string; description: string | string[] }[][];
    userCareerInfoList?: { term: string; description: string | string[] }[][];
    userAchievementsInfoList?: { term: string; description: string | string[] }[][];
    activityHistories?: ActivityHistoriesProps[];
  };
  hopedConditions: {
    basicHopedConditions?: basicHopedConditionsProps;
    detailHopedConditions?: detailHopedConditionsProps;
  };
  userProfileInformation: {
    thoughtsAndSelfPr?: thoughtsAndSelfPrProps;
    userSkills?: userSkillsProps[];
    userProductPhotos?: userProductPhotoProps[];
  };
  pickupButtonContent?: PickupButtonContentProps;
  scoutButtonContent: scoutButtonContentProps;
  userHiddenButtonContent: UserHiddenButtonContent;
};

type ModelessDialogInnerProps = {
  isShowScoutButton: boolean;
  showReadMoreButtonControlInformation: {
    showReadMoreButton?: boolean;
    maxDisplayLargeTextHeight?: number;
    maxDisplaySmallTextHeight?: number;
  };
} & UserDetailModalProps;

type Props = {
  content: UserDetailModalProps | null;
  isShowScoutButton: boolean;
  handleOnClickClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
  showReadMoreButtonControlInformation: {
    showReadMoreButton?: boolean;
    maxDisplayLargeTextHeight?: number;
    maxDisplaySmallTextHeight?: number;
  };
};

// スキル・経験業務
// 要素の高さが160px以上
const USER_SKILLS_MAX_SIZE = 160;

const ModelessDialogInner: FC<ModelessDialogInnerProps> = ({
  gaEvents,
  displayControlInformation,
  userData,
  historyInformation,
  hopedConditions,
  userProfileInformation,
  pickupButtonContent,
  scoutButtonContent,
  userHiddenButtonContent,
  isShowScoutButton,
  showReadMoreButtonControlInformation: {
    showReadMoreButton = false,
    maxDisplayLargeTextHeight = 0,
    maxDisplaySmallTextHeight = 0,
  },
}) => {
  return (
    <div
      className={styles.contents}
      data-ga-label={gaEvents.modal.gaLabel}
      data-ga-action={gaEvents.modal.gaAction}
    >
      <Header
        isShowScoutButton={isShowScoutButton}
        displayControlInformation={displayControlInformation}
        sexAndAgeText={userData.sexAndAgeText}
        scoutButtonContent={scoutButtonContent}
        pickupButtonContent={pickupButtonContent}
        scoutGaEvents={gaEvents.scout}
        premiumScoutGaEvents={gaEvents.premiumScout}
        pickupGaEvents={gaEvents.pickup}
        pickupReleaseGaEvents={gaEvents.pickupRelease}
      />
      {displayControlInformation.isHideInfo ? (
        <>
          {displayControlInformation.hideInfoReasonText && (
            <div className="box-scout-error-information m_20">
              <p className="text-scout-error-information t_center">
                {displayControlInformation.hideInfoReasonText}
                <br />
                <a href={displayControlInformation.scoutIndexUrl}>スカウト検索</a>
                で他の求職者も探してみませんか？
              </p>
            </div>
          )}
        </>
      ) : (
        <>
          {displayControlInformation.isNotScoutable &&
            displayControlInformation.notScoutableReasonText && (
              <div className="box-scout-error-information m_20">
                <p className="text-scout-error-information t_center">
                  {displayControlInformation.notScoutableReasonText}
                  <br />
                  <a href={displayControlInformation.scoutIndexUrl}>スカウト検索</a>
                  で他の求職者も探してみませんか？
                </p>
              </div>
            )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <p className={styles.basicInfoText}>
              他社からのスカウト受信数:{userData.acceptableReceivedScoutCount}件 最終アクセス日:
              {userData.currentAccessAt}
            </p>
            {/* 求職者を非表示にする機能のボタン */}
            {userHiddenButtonContent?.isHidden !== undefined &&
              userHiddenButtonContent?.isLoading !== undefined && (
                <ResultUserControlButton
                  isHidden={userHiddenButtonContent.isHidden}
                  isLoading={userHiddenButtonContent.isLoading}
                  onClickUserHiddenTrigger={() =>
                    userHiddenButtonContent.onClickUserHiddenTrigger
                      ? userHiddenButtonContent.onClickUserHiddenTrigger()
                      : undefined
                  }
                />
              )}
          </div>
          {userData.multiActionInfo && userData.multiActionInfo.length > 0 && (
            <MultiActionInformationBox
              multiActionInfo={userData.multiActionInfo as MinOneArray<MultiActionItem>}
              isIconFire={displayControlInformation.isIconFire}
            />
          )}
          {userData.photoContestInfo && userData.photoContestInfo.length > 0 && (
            <div className={styles.photoContestLabelList}>
              {userData.photoContestInfo.map((info) => (
                <div key={info.title} className={styles.photoContestLabelItem}>
                  <PhotoContestLabel rank={info.rank} title={info.title} />
                </div>
              ))}
            </div>
          )}
          {hopedConditions.basicHopedConditions && (
            <section className={styles.contentsMarginMedium}>
              <BasicHopedConditionsTable
                basicHopedConditions={hopedConditions.basicHopedConditions}
              />
            </section>
          )}
          {hopedConditions.detailHopedConditions && (
            <section className={styles.sectionContainer}>
              <SectionHeading>希望条件の詳細</SectionHeading>
              <div className={styles.contentsMarginSmall}>
                <DetailHopedConditionsTable
                  detailHopedConditions={hopedConditions.detailHopedConditions}
                  maxDisplayLargeTextHeight={maxDisplayLargeTextHeight}
                  maxDisplaySmallTextHeight={maxDisplaySmallTextHeight}
                  showReadMoreButton={showReadMoreButton}
                />
              </div>
            </section>
          )}
          {historyInformation.userSchoolInfoList &&
            historyInformation.userSchoolInfoList.length > 0 && (
              <section className={styles.listSectionContainer}>
                <SectionHeading>学歴</SectionHeading>
                <ReadMoreButton size="large">
                  <UserSchoolsDefinitionList
                    userSchoolInfoList={historyInformation.userSchoolInfoList}
                  />
                </ReadMoreButton>
              </section>
            )}
          {historyInformation.userCareerInfoList &&
            historyInformation.userCareerInfoList.length > 0 && (
              <section className={styles.listSectionContainer}>
                <SectionHeading>職務経歴</SectionHeading>
                <ReadMoreButton size="large">
                  <UserCareerDefinitionList
                    userCareerInfoList={historyInformation.userCareerInfoList}
                  />
                </ReadMoreButton>
              </section>
            )}
          {userProfileInformation.userSkills && (
            <section className={styles.sectionContainer}>
              <SectionHeading>スキル・経験業務</SectionHeading>
              <div className={styles.contentsMarginSmall}>
                <ReadMoreButton size="large">
                  <UserSkillsList
                    userSkills={userProfileInformation.userSkills}
                    maxSize={USER_SKILLS_MAX_SIZE}
                  />
                </ReadMoreButton>
              </div>
            </section>
          )}
          {userProfileInformation.userProductPhotos &&
            userProfileInformation.userProductPhotos.length > 0 && (
              <section className={styles.sectionContainer}>
                <SectionHeading>求職者を表現する画像</SectionHeading>
                <ReadMoreButton size="large">
                  <Suspense fallback={null}>
                    <UserProductPhotosCassette
                      userProductPhotos={userProfileInformation.userProductPhotos}
                    />
                  </Suspense>
                </ReadMoreButton>
              </section>
            )}
          {historyInformation.userAchievementsInfoList &&
            historyInformation.userAchievementsInfoList.length > 0 && (
              <section className={styles.listSectionContainer}>
                <SectionHeading>受賞・表彰歴</SectionHeading>
                <ReadMoreButton size="large">
                  <UserAchievementsDefinitionList
                    userAchievementsInfoList={historyInformation.userAchievementsInfoList}
                  />
                </ReadMoreButton>
              </section>
            )}
          {userProfileInformation.thoughtsAndSelfPr && (
            <section className={styles.sectionContainer}>
              <SectionHeading>想い・自己PR</SectionHeading>
              <ThoughtsAndSelfPrTable
                thoughtsAndSelfPr={userProfileInformation.thoughtsAndSelfPr}
              />
            </section>
          )}
          <section className={styles.sectionContainer}>
            <SectionHeading>活動履歴</SectionHeading>
            {historyInformation.activityHistories ? (
              <ReadMoreButton size="large">
                <ActivityHistoriesTable activityHistories={historyInformation.activityHistories} />
              </ReadMoreButton>
            ) : (
              <InformationBox className={styles.activityHistoryInformationBox}>
                <p>履歴はありません</p>
              </InformationBox>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export const Presentation: FC<Props> = ({
  content,
  isShowScoutButton,
  handleOnClickClose,
  showReadMoreButtonControlInformation,
}) => {
  return (
    <ModelessDialog isOpen onClickClose={handleOnClickClose}>
      {content ? (
        <ModelessDialogInner
          {...content}
          isShowScoutButton={isShowScoutButton}
          showReadMoreButtonControlInformation={showReadMoreButtonControlInformation}
        />
      ) : (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
        </div>
      )}
    </ModelessDialog>
  );
};
